import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Popconfirm,
  Row,
  Table,
  Tooltip,
  message,
} from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { LoadingOutlined, CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { translate } from "../../locale";
import ProtocolServices from "../../services/ProtocolServices";
import _ from "lodash";
import { TableHelper } from "../../helpers/TableHelper";
import { AdminContext } from "../../providers/AdminContext";
import AddProtocol from "../Modals/AddProtocol";
import SecondaryButton from "../Buttons/SecondaryButton";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const ProtocolsTable = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
    searchText: "",
  });
  const [total, setTotal] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const { user } = useContext(AdminContext);

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2">
          <Tooltip title={translate("Ver protocolo")} color="blur">
            <Button
              type="text"
              icon={<ArrowRightOutlined />}
              size="small"
              className="border-0"
              onClick={() => navigate(`/admin/protocols/${record.id}`)}
            />
          </Tooltip>
          {isSuperAdmin && (
            <Popconfirm
              title={translate("¿Estás seguro de clonar el registro?")}
              okText={translate("Si")}
              cancelText={translate("No")}
              onConfirm={() => handleClone(record.id)}
            >
              <Tooltip title={translate("Clonar")} color="blur">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  size="small"
                  className="border-0"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
    {
      title: translate("Nombre"),
      dataIndex: "name",
      key: "name",
      sorter: { multiple: 2 },
    },
    {
      title: translate("Duración"),
      dataIndex: "duration",
      key: "duration",
      sorter: { multiple: 1 },
    },
  ];

  useEffect(() => {
    loadProtocols();
    setIsSuperAdmin(user.user.rol === "eira-super-admin");
  }, [JSON.stringify(tableParams)]);

  const loadProtocols = async () => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null && data.page_size !== 0) {
      data = { ...data, order_by: sorter };
    }
    if (tableParams.searchText !== "") {
      data = { ...data, name: tableParams.searchText };
    }
    setLoading(true);
    try {
      if (data.page_size !== 0) {
        const res = await ProtocolServices.getProtocols(data);
        if (
          res.status >= 200 &&
          res.status < 300 &&
          res.data.protocols.length > 0
        ) {
          setData(res.data.protocols);
          setTotal(res.data.total_pages * tableParams.pagination.pageSize);
        }
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleClone = async (protocol_id) => {
    setLoading(true);
    try {
      const res = await ProtocolServices.clone({ protocol_id: protocol_id });
      if (res.status >= 200 && res.status < 300) {
        loadProtocols();
        navigate(`/admin/protocols/${res.data.data.id}`);
      } else {
        message.error(
          translate(
            "Ocurrió un error durante el proceso de {0}",
            translate("clonación")
          ) + ` / Error ${res.status}`,
          30
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("error", error);
      message.error(error.data.message);
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
  };

  const handleModalComplete = () => {
    // update user list
    setModalVisible(false);
    loadProtocols();
  };

  const handleAddProtocol = () => {
    setModalVisible(true);
  };
  const searchData = (value) => {
    setTableParams((v) => ({
      pagination: {
        ...v.pagination,
        current: 1,
      },
      searchText: value,
    }));
  };

  return (
    <>
      <Row gutter={16}>
        <Col className="gutter-row" span="18">
          <SecondaryButton
            type="primary"
            className="bg-custom_primary border-custom_primary"
            onClick={handleAddProtocol}
          >
            {translate("Agregar protocolo")}
          </SecondaryButton>
          <AddProtocol
            protocolId={0}
            visible={modalVisible}
            onCancel={handleModalCancel}
            onComplete={handleModalComplete}
          />
        </Col>
        <Col span="6" className="mb-4">
          <Form>
            <Form.Item>
              <Input.Search
                allowClear
                placeholder={translate("Buscar")}
                onSearch={searchData}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={data}
        columns={columns}
        loading={
          loading && { indicator: antIcon, className: "text-custom_blue" }
        }
        rowKey={(i) => i.id}
        locale={{
          emptyText: <Empty description={translate("No hay datos")} />,
          sortTitle: translate("Ordenar"),
          triggerDesc: translate("Ordenar (descendente)"),
          triggerAsc: translate("Ordenar (ascendente)"),
          cancelSort: translate("Cancelar orden"),
        }}
        pagination={{ ...tableParams.pagination, total: total }}
        onChange={handleTableChange}
      />
    </>
  );
};

export default ProtocolsTable;
